import React from "react";
import { toast } from "react-toastify";
import {
    ModeEditOutlineTwoTone,
    CheckCircleOutline,
    CancelOutlined,
} from "@mui/icons-material";
import {
    Box,
    CircularProgress,
    IconButton,
    Typography,
    Switch,
    Tooltip,
} from "@mui/material";

import AppTable from "components/Shared/AppTable";
import FormDrawer from "components/Shared/FormDrawer";
import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import { useTablePagination } from "hooks";

import {
    useLazyGetVendorShippingRateQuery,
    useCreateVendorShippingRateMutation,
    useUpdateVendorShippingRateMutation,
} from "store/services/vendorProfileApi";

import ShippingRateForm from "./ShippingRateForm";

const ShippingRateList = ({ vendorId }) => {
    const [showForm, setShowForm] = React.useState(false);
    const [selectedRate, setSelectedRate] = React.useState(null);

    const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
        useTablePagination();

    const [
        fetchShippingRate,
        { data: rateRes, isLoading, isFetching },
    ] = useLazyGetVendorShippingRateQuery();

    const [createRate, { isLoading: creating }] = useCreateVendorShippingRateMutation();
    const [updateRate, { isLoading: updating }] = useUpdateVendorShippingRateMutation();

    // Toggle Active Status
    const handleToggleActive = async (rate) => {
        try {
            const newActiveState = !rate.isActive;

            await updateRate({
                vendorId,
                rateId: rate._id,
                isActive: newActiveState,
                // shippingRate remains unchanged
            }).unwrap();

            toast.success(
                newActiveState
                    ? "Shipping rate activated! Others deactivated."
                    : "Shipping rate deactivated."
            );

            // Refetch to reflect changes
            fetchShippingRate({ vendorId, page: currentPage, limit: perPage });
        } catch (err) {
            toast.error(err?.data?.message || "Failed to update status");
        }
    };

    const columns = [
        {
            name: "Shipping Rate (Rs)",
            selector: (row) => row?.shippingRate,
            cell: (row) => <strong>Rs {row?.shippingRate?.toLocaleString()}</strong>,

        },
        {
            name: "Created By",
            selector: (row) => row?.createdBy?.name || "N/A",
            cell: (row) => <span>{row?.createdBy?.name || "N/A"}</span>,

        },
        {
            name: "Updated By",
            selector: (row) => row?.updatedBy?.name || "N/A",
            cell: (row) => <span>{row?.updatedBy?.name || "N/A"}</span>,

        },
        {
            name: "Status",
            cell: (row) => (
                <Box display="flex" alignItems="center" gap={1}>
                    {row.isActive ? (
                        <CheckCircleOutline color="success" fontSize="small" />
                    ) : (
                        <CancelOutlined color="error" fontSize="small" />
                    )}
                    <Typography
                        variant="body2"
                        color={row.isActive ? "success.main" : "error.main"}
                        fontWeight={500}
                    >
                        {row.isActive ? "Active" : "Inactive"}
                    </Typography>
                </Box>
            ),
        },
        {
            name: "Actions",
            cell: (row) => (
                <>
                    <Tooltip
                        title={row.isActive
                            ? "Currently Active Rate"
                            : "Make this the active rate (others will be deactivated)"}
                    >
                        <Switch
                            checked={row.isActive}
                            onChange={() => handleToggleActive(row)}
                            disabled={updating}
                            color="primary"
                            size="medium" />
                    </Tooltip>
                    <IconButton
                        size="small"
                        onClick={() => {
                            setSelectedRate(row);
                            setShowForm(true);
                        }}
                    >
                        <ModeEditOutlineTwoTone />
                    </IconButton></>
            ),
            ignoreRowClick: true,
            right: true,
        },
    ];

    const handleSubmit = async (payload) => {
        try {
            if (selectedRate) {
                await updateRate({
                    vendorId,
                    rateId: selectedRate._id,
                    ...payload,
                }).unwrap();
                toast.success("Shipping rate updated successfully!");
            } else {
                await createRate(payload).unwrap();
                toast.success("Shipping rate created successfully!");
            }
            handleCloseForm();
            fetchShippingRate({ vendorId, page: currentPage, limit: perPage });
        } catch (err) {
            toast.error(err?.data?.message ?? "Operation failed!");
        }
    };

    const handleCloseForm = () => {
        setSelectedRate(null);
        setShowForm(false);
    };

    React.useEffect(() => {
        if (!vendorId) return;
        fetchShippingRate({
            vendorId,
            page: currentPage,
            limit: perPage,
        });
    }, [vendorId, currentPage, perPage, fetchShippingRate]);

    return (
        <div>
            <BreadcrumbLayout.Paper>
                {rateRes ? (
                    <AppTable
                        title="Vendor Shipping Rates"
                        columns={columns}
                        data={rateRes?.data ?? []}
                        progressPending={isLoading || isFetching || updating}
                        paginationServer
                        paginationPerPage={perPage}
                        paginationTotalRows={rateRes?.totalItems ?? 0}
                        paginationDefaultPage={currentPage}
                        onChangePage={handlePageChange}
                        onChangeRowsPerPage={handlePerRowsChange}
                    />
                ) : (
                    <Box display="flex" justifyContent="center" py={4}>
                        <CircularProgress />
                        <Typography ml={2}>Loading Shipping Rates...</Typography>
                    </Box>
                )}
            </BreadcrumbLayout.Paper>

            <FormDrawer
                title={selectedRate ? "Edit Shipping Rate" : "Add Shipping Rate"}
                open={showForm}
                onClose={handleCloseForm}
            >
                <ShippingRateForm
                    vendorId={vendorId}
                    shippingRateData={selectedRate}
                    onSubmit={handleSubmit}
                    onCancel={handleCloseForm}
                    isBusy={creating || updating}
                />
            </FormDrawer>
        </div>
    );
};

export default ShippingRateList;